import {
  Attribution,
  CloseOutlined,
  ControlPointOutlined,
  InfoOutlined,
  Person,
  Search,
} from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useState } from "react";

import { useDebouncedEffect } from "../../../../../hooks/useDebounceEffect";
import useService from "../../../../../hooks/useService";
import axios from "../../../../api/axios";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import FormSubmitButton from "../../../../global/FormComponents/FormSubmitButton";
import ModalButton from "../../../../global/ModalButton";
import ShowMoreButton from "../../../../global/ShowMoreButton";

const CaseAssignmentModal = ({
  caseId,
  revalidateParent,
  setDisplayLoading,
  setError,
  setErrorMessage,
  setOpenAssignUser,
  setOpenSuccess,
}) => {
  const enumRepository = useService("repositories/enum-repository");

  const [list, setList] = useState({
    users: [],
    roles: [],
  });
  const [formData, setFormData] = useState({
    user: null,
    role: null,
  });
  const [showMore, setShowMore] = useState({
    users: false,
    roles: false,
  });
  const [search, setSearch] = useState({
    users: "",
    roles: "",
  });
  const [limit, setLimit] = useState({
    users: 25,
    roles: 25,
  });
  const [loading, setLoading] = useState({
    users: true,
    roles: false,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const updateStateFor = (setter) => (key, value) => setter((prev) => ({ ...prev, [key]: value }));
  const updateLoading = updateStateFor(setLoading);
  const updateList = updateStateFor(setList);
  const updateShowMore = updateStateFor(setShowMore);
  const updateSearch = updateStateFor(setSearch);
  const updateLimit = updateStateFor(setLimit);

  const handleSubmit = () => {
    if (!formData.role || !formData.user) {
      setError(true);
      setErrorMessage(["All frields required, please choose a user and a role"]);
      setSubmitLoading(false);
      return;
    }

    setSubmitLoading(true);
    setDisplayLoading(true);
    const body = {
      case: caseId,
      role: formData.role.key,
      user: formData.user.key,
    };

    axios
      .post("/api/user_case/", body, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        if (response.status < 400) {
          setOpenSuccess(true);
          setSubmitLoading(false);
          setDisplayLoading(false);
          revalidateParent();
          closeAssignModal();
        }
      })
      .catch(function (error) {
        const res = error.response.data;
        const errArr = ApiRequestErrorHandler(res);
        setErrorMessage(errArr);
        setError(true);
        setSubmitLoading(false);
        setDisplayLoading(false);
      });
  };

  const selectUserForAssign = (user) => {
    setFormData((prev) => ({ ...prev, user: user }));
    updateSearch("users", "");
  };

  const closeAssignModal = () => {
    setOpenAssignUser(false);
    setFormData({ user: null, role: null });
    setSearch({ users: "", roles: "" });
  };

  const optionConfigs = {
    users: {
      queryPath: "core/dynamic-options/assignable-users/",
      buildParams: (searchTerm, resLimit) =>
        `limit=${resLimit}&ordering=name${searchTerm ? `&search=${searchTerm}` : ""}`,
      hasMore: (count, resLimit) => count >= resLimit,
      placeholder: "Search User",
      containerClassName: "w-full mb-4",
      listId: "user-list-container",
      ariaLabel: "Available Case Managers",
      onSelect: (item) => selectUserForAssign(item),
    },
    roles: {
      queryPath: "core/dynamic-options/user-roles/",
      buildParams: (searchTerm, resLimit) =>
        `limit=${resLimit}${searchTerm ? `&search=${searchTerm}` : ""}`,
      hasMore: (count, resLimit) => count > resLimit,
      placeholder: "Search User Roles",
      containerClassName: "w-full mx-0 my-2",
      listId: "roles-list-container",
      ariaLabel: "Available Roles",
      onSelect: (item) => setFormData((prev) => ({ ...prev, role: item })),
    },
  };

  async function searchOptions(type) {
    const config = optionConfigs[type];
    updateLoading(type, true);
    updateShowMore(type, false);
    updateList(type, []);
    try {
      const resp = await enumRepository.query({
        urlPath: `${config.queryPath}?${config.buildParams(search[type], limit[type])}`,
      });
      const count = await enumRepository.getTotalCount();
      updateShowMore(type, config.hasMore(count, limit[type]));
      updateList(type, resp);
    } catch (error) {
      console.error(error);
      return error;
    } finally {
      updateLoading(type, false);
    }
  }

  useDebouncedEffect(() => searchOptions("users"), [search.users, limit.users], 500);

  useDebouncedEffect(() => searchOptions("roles"), [search.roles, limit.roles], 500);

  const renderOptionSelector = (type) => {
    const config = optionConfigs[type];
    return (
      <div className={config.containerClassName}>
        <div className="flex w-full items-center gap-3">
          <TextField
            className="w-full"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={(event) => updateSearch(type, event.target.value)}
            placeholder={config.placeholder}
            size="small"
            value={search[type]}
          />
          {search[type] && (
            <ModalButton
              action={() => updateSearch(type, "")}
              baseColor="#d32f2f"
              className="block"
              content="Reset"
            />
          )}
        </div>
        <div
          className="mt-2 h-[150px] w-full overflow-y-auto"
          id={config.listId}
          aria-label={config.ariaLabel}
          value={type === "users" ? formData.user?.key : formData.role?.key || ""}
        >
          {loading[type] ? (
            <div className="mx-auto w-fit">
              <CircularProgress color="secondary" />
            </div>
          ) : list[type].length === 0 ? (
            "No Results"
          ) : (
            list[type].map((option, idx) => {
              return (
                <MenuItem
                  value={option.key}
                  key={option.key ?? idx}
                  onClick={() => config.onSelect(option)}
                  style={{ justifyContent: "space-between" }}
                >
                  {option?.label || "N/A"}
                </MenuItem>
              );
            })
          )}
          <ShowMoreButton
            showMoreVisible={showMore[type]}
            setResLimit={(newLimit) => updateLimit(type, newLimit)}
            resLimit={limit[type]}
            showMoreAmt={25}
          />
        </div>
      </div>
    );
  };

  const renderSelectionSummary = ({ label, Icon, value, onRemove, removeId }) => {
    if (!value) {
      return null;
    }

    return (
      <>
        <h3 className="w-full text-base text-gray-500">{label}</h3>
        <div className="mb-4 flex w-full items-center justify-between rounded-md border-2 border-blue-700 p-2">
          <div className="flex items-center gap-2 text-lg">
            <Icon />
            <h3>{value.label}</h3>
          </div>
          <Tooltip title="Remove Selection">
            <button
              className="h-4 w-4 rounded-full border border-red-500 bg-transparent text-[10px] text-red-500"
              id={removeId}
              onClick={onRemove}
            >
              x
            </button>
          </Tooltip>
        </div>
      </>
    );
  };

  return (
    <Dialog open>
      <div className="p-4">
        {/* Modal Title */}
        <div className="flex">
          <InfoOutlined color="primary" />
          <span className="ml-2 font-medium text-blue-900">Assignment</span>
          <Button className="ml-auto flex justify-end p-0" onClick={() => closeAssignModal()}>
            <CloseOutlined />
          </Button>
        </div>
        <h1 className="mx-0 my-4 text-center text-2xl text-blue-900">Assign User To Case</h1>
        <div className="flex min-w-[400px] flex-col items-center justify-center">
          {formData.user
            ? renderSelectionSummary({
                label: "User",
                Icon: Person,
                value: formData.user,
                onRemove: () => {
                  updateSearch("users", "");
                  setFormData((prev) => ({ ...prev, user: null }));
                },
                removeId: "remove-user-btn",
              })
            : renderOptionSelector("users")}

          {formData.user && !formData.role && renderOptionSelector("roles")}

          {renderSelectionSummary({
            label: "Role",
            Icon: Attribution,
            value: formData.role,
            onRemove: () => {
              updateSearch("roles", "");
              setFormData((prev) => ({ ...prev, role: null }));
            },
            removeId: "remove-role-btn",
          })}

          <FormSubmitButton
            disabled={!formData.role || !formData.user}
            isLoading={submitLoading}
            handleSubmit={handleSubmit}
            buttonText="Assign"
            buttonColor="sky"
            ButtonIcon={<ControlPointOutlined />}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CaseAssignmentModal;
